/**
 * Adapts MPV's state into the `MapControls` surface the agent chat's client
 * tools call. Everything here is a thin binding onto handles MPV already owns
 * (`mapStateDispatch`, `changeSelection`, `setTabbedSidebarState`, the mapbox
 * instance) — no map internals are reached into.
 */

import * as I from 'immutable';
import React from 'react';
import {useNavigate} from 'react-router-dom';

import {inAppPath} from 'app/components/AgentChat/links';
import {
  CurrentView,
  FocusNoteInput,
  MapControls,
  PaneName,
  ResolvedProperty,
  SelectPropertyInput,
  SelectPropertyResult,
} from 'app/components/AgentChat/types';
import {SidebarViewState} from 'app/components/TabbedSidebar/TabbedSidebar';
import {ApiFeature} from 'app/modules/Remote/Feature';
import {ApiOrganization} from 'app/modules/Remote/Organization';
import {ApiProject} from 'app/modules/Remote/Project';
import {FeatureSelection} from 'app/pages/ProjectDashboardSwitch';
import {PolygonState} from 'app/providers/MapPolygonStateProvider';
import {NotesState} from 'app/stores/NotesStore';
import {NOTE_TYPE_AGENT} from 'app/utils/constants';
import * as layerUtils from 'app/utils/layerUtils';
import * as mapUtils from 'app/utils/mapUtils';
import {Mode} from 'app/utils/mapUtils/modes';
import * as routeUtils from 'app/utils/routeUtils';

import {ClickableNote} from './useAgentRunNotes';
import {MapStateDispatch} from '../MapStateProvider';
import {MapInteractionTool} from '../view';

interface Args {
  organization: I.ImmutableOf<ApiOrganization>;
  selectedProject: I.ImmutableOf<ApiProject>;
  selectedFeature: I.ImmutableOf<ApiFeature> | null;
  selectedFeatures: I.Set<I.ImmutableOf<ApiFeature>>;
  features: I.ImmutableOf<ApiFeature[]>;
  imageRefs: mapUtils.MapImageRefs;
  mode: Mode;
  mapStateDispatch: MapStateDispatch;
  mapboxMap: mapboxgl.Map | null;
  getLatestCameraOptions: () => mapUtils.CameraOptions | null;
  changeSelection: (selection: FeatureSelection) => void;
  tabbedSidebarState: SidebarViewState;
  setTabbedSidebarState: React.Dispatch<React.SetStateAction<SidebarViewState>>;
  openAnalyzeArea: () => void;
  activeMapInteractionTool: MapInteractionTool | null;
  isOverlayVisibleByName: Record<string, boolean>;
  drawnPolygon: PolygonState['polygon'];
  focusedNoteId: NotesState['focusedNoteId'];
  /** MPV's own observation-click handler, shared with the Agent tab. */
  onNoteClick: (note: ClickableNote) => void;
}

// Sidebar tabs are addressed by their display title.
const PANE_TITLES = {
  notes: 'Notes',
  details: 'Details',
  order: 'Order',
} as const;

const PANE_BY_TITLE: Record<string, PaneName> = Object.fromEntries(
  Object.entries(PANE_TITLES).map(([pane, title]) => [title, pane as PaneName])
);

// A multi-part property's part rides on its name, as formatFeatureName, the cart and the backend's
// tools write it, so the chat matches it to theirs.
function featureName(feature: I.ImmutableOf<ApiFeature>): string {
  const name = (feature.getIn(['properties', 'name']) as string | undefined) ?? '';
  const part = feature.getIn(['properties', 'multiFeaturePartName']) as string | undefined;
  return part ? `${name} - ${part}` : name;
}

function featureLensId(feature: I.ImmutableOf<ApiFeature>): string {
  return feature.getIn(['properties', 'lensId']) as string;
}

export default function useChatMapControls({
  organization,
  selectedProject,
  selectedFeature,
  selectedFeatures,
  features,
  imageRefs,
  mode,
  mapStateDispatch,
  mapboxMap,
  getLatestCameraOptions,
  changeSelection,
  tabbedSidebarState,
  setTabbedSidebarState,
  openAnalyzeArea,
  activeMapInteractionTool,
  isOverlayVisibleByName,
  drawnPolygon,
  focusedNoteId,
  onNoteClick,
}: Args): MapControls {
  const navigate = useNavigate();

  const getCurrentView = React.useCallback((): CurrentView => {
    const {sourceId} = layerUtils.parseLayerKey(imageRefs[0].layerKey);

    // imageRefs are ordered ascending by date, so index 0 is the "before"
    // scene in compare mode and the only scene otherwise.
    const compareDate = mode === 'COMPARE' ? (imageRefs[1]?.cursor ?? null) : null;
    const compareLayerKey = mode === 'COMPARE' ? (imageRefs[1]?.layerKey ?? null) : null;

    const latestCamera = getLatestCameraOptions();
    const camera = mapboxMap
      ? {
          center: [mapboxMap.getCenter().lng, mapboxMap.getCenter().lat] as [number, number],
          zoom: mapboxMap.getZoom(),
        }
      : latestCamera
        ? {
            center: [latestCamera.center.lng, latestCamera.center.lat] as [number, number],
            zoom: latestCamera.zoom,
          }
        : null;

    // The camera says where the map is pointed; the bounds say how much ground
    // that covers, which is what "the properties on screen" means.
    const mapBounds = mapboxMap?.getBounds();
    const bounds: CurrentView['bounds'] = mapBounds
      ? [
          [mapBounds.getWest(), mapBounds.getSouth()],
          [mapBounds.getEast(), mapBounds.getNorth()],
        ]
      : null;

    // Absolute URLs, so the agent can hand them back as links in its replies.
    //
    // The backend keys these URLs on the organization id's first 8 characters, and
    // `makeUuidPrefix` only truncates uuids — a Firebase-era id survives whole. Two
    // spellings of the same property are two known links, and the model repairing a
    // map id then finds it under two paths and gives up, so the view mints the
    // backend's spelling.
    const organizationKey = organization.get('id').slice(0, 8);

    const propertyEntity = (feature: I.ImmutableOf<ApiFeature>) => ({
      id: featureLensId(feature),
      name: featureName(feature),
      lensUrl: routeUtils.makeProjectDashboardUrl(
        organizationKey,
        selectedProject,
        'map',
        [feature],
        undefined,
        {
          includeRoot: true,
        }
      ),
    });

    return {
      organization: {id: organization.get('id'), name: organization.get('name')},
      portfolio: {
        id: selectedProject.get('id'),
        name: selectedProject.get('name'),
        lensUrl: routeUtils.makeProjectDashboardUrl(
          organizationKey,
          selectedProject,
          'map',
          undefined,
          undefined,
          {
            includeRoot: true,
          }
        ),
      },
      property: selectedFeature ? propertyEntity(selectedFeature) : null,
      selectedProperties: selectedFeatures.toArray().map(propertyEntity),
      sourceId,
      layerKey: imageRefs[0].layerKey,
      date: imageRefs[0].cursor,
      compareDate,
      compareSourceId: compareLayerKey && layerUtils.parseLayerKey(compareLayerKey).sourceId,
      compareLayerKey,
      mode,
      camera,
      bounds,
      // A collapsed sidebar shows no pane, whichever tab it would open on.
      openPane: tabbedSidebarState.isExpanded
        ? (PANE_BY_TITLE[tabbedSidebarState.currentViewTitle ?? ''] ?? null)
        : null,
      activeTool: activeMapInteractionTool,
      visibleOverlays: Object.entries(isOverlayVisibleByName)
        .filter(([, isVisible]) => isVisible)
        .map(([name]) => name),
      drawnGeometry: drawnPolygon,
      focusedNoteId: focusedNoteId === null ? null : String(focusedNoteId),
    };
  }, [
    organization,
    selectedProject,
    selectedFeature,
    selectedFeatures,
    imageRefs,
    mode,
    mapboxMap,
    getLatestCameraOptions,
    tabbedSidebarState,
    activeMapInteractionTool,
    isOverlayVisibleByName,
    drawnPolygon,
    focusedNoteId,
  ]);

  const flyTo = React.useCallback(
    ({center, zoom}: {center: [number, number]; zoom?: number}) => {
      // In compare mode both panes report themselves as the instance, so this
      // drives whichever mounted last; the panes' cameras are synced anyway.
      mapboxMap?.flyTo(zoom === undefined ? {center} : {center, zoom});
    },
    [mapboxMap]
  );

  const selectProperty = React.useCallback(
    ({property, lensUrl}: SelectPropertyInput): SelectPropertyResult => {
      const match =
        features.find((f) => !!f && routeUtils.featureMatchesParam(f, property)) ??
        features.find((f) => !!f && featureName(f).toLowerCase() === property.toLowerCase()) ??
        features.find((f) => !!f && featureName(f).toLowerCase().includes(property.toLowerCase()));

      if (match) {
        changeSelection({
          organizationId: organization.get('id'),
          projectId: selectedProject.get('id'),
          featureIds: I.Set([featureLensId(match)]),
        });
        return {message: `Selected ${featureName(match)}.`};
      }

      // Only this portfolio's properties are loaded, so one from anywhere else is
      // reached by its own page. `inAppPath` keeps the route to Lens, and the
      // destination opens its Chat tab so the conversation comes along.
      const path = inAppPath(lensUrl);
      if (path) {
        navigate(path, {state: routeUtils.OPEN_CHAT_STATE});
        return {message: `Opening ${property} in its own portfolio.`, navigated: true};
      }

      return {
        message:
          `No property in ${selectedProject.get('name')} matches "${property}". ` +
          'Only this portfolio is loaded — to reach a property in another one, call ' +
          'select_property again with the lensUrl from the tool result that named it.',
      };
    },
    [features, organization, selectedProject, changeSelection, navigate]
  );

  const describeProperty = React.useCallback(
    (property: string): ResolvedProperty | null => {
      const match = features.find((f) => !!f && routeUtils.featureMatchesParam(f, property));
      if (!match) {
        return null;
      }
      const featureCollectionId: number = match.getIn(['properties', 'featureCollectionId']);
      return {
        feature: match,
        featureId: match.get('id'),
        featureCollectionId,
        projectId: selectedProject.get('id'),
        projectName: selectedProject.get('name'),
      };
    },
    [features, selectedProject]
  );

  const findProperty = React.useCallback(
    (property: string) => {
      const resolved = describeProperty(property);
      return resolved ? {featureId: resolved.featureId} : null;
    },
    [describeProperty]
  );

  // A note only focuses on the property it belongs to: selecting a property
  // remounts the notes store around the new feature, which drops any focus set
  // before the switch. So hold the note until its property is on screen.
  const pendingFocusRef = React.useRef<{featureId: number; note: ClickableNote} | null>(null);

  React.useEffect(() => {
    const pending = pendingFocusRef.current;
    if (!pending || selectedFeature?.get('id') !== pending.featureId) {
      return;
    }
    pendingFocusRef.current = null;
    onNoteClick(pending.note);
  }, [selectedFeature, onNoteClick]);

  const focusNote = React.useCallback(
    ({noteId, propertyId, geometry}: FocusNoteInput) => {
      const note: ClickableNote = {
        id: noteId,
        noteType: NOTE_TYPE_AGENT,
        geometry: geometry ?? null,
      };
      const target = propertyId ? findProperty(propertyId) : null;

      if (target && target.featureId !== selectedFeature?.get('id')) {
        pendingFocusRef.current = {featureId: target.featureId, note};
        selectProperty({property: propertyId!});
        return;
      }
      onNoteClick(note);
    },
    [findProperty, selectProperty, selectedFeature, onNoteClick]
  );

  const setLayerAndDate = React.useCallback(
    (update: {sourceId?: string; layerKey?: string; date?: string; compareDate?: string}) => {
      const current = layerUtils.parseLayerKey(imageRefs[0].layerKey);
      const nextSourceId = update.sourceId ?? current.sourceId;

      // `layerKey` arrives either as the app's composite "<source>_<layer>" or
      // as the bare layer id alongside `sourceId`; accept both.
      let nextLayerKey = imageRefs[0].layerKey;
      if (update.layerKey) {
        nextLayerKey = update.layerKey.startsWith(`${nextSourceId}_`)
          ? update.layerKey
          : layerUtils.getLayerKeyFromSourceAndLayer(nextSourceId, update.layerKey);
      } else if (update.sourceId) {
        nextLayerKey = layerUtils.getLayerKeyFromSourceAndLayer(nextSourceId, current.layerId);
      }

      // A call that names no layer only moves dates, so the compare pane keeps
      // the layer it is showing rather than adopting the primary pane's. Each
      // layer stays attached to its own cursor through the sort, since the new
      // date can land on either side of the compare date.
      const namesLayer = !!(update.layerKey || update.sourceId);
      const compareLayerKey = mode === 'COMPARE' ? imageRefs[1]?.layerKey : undefined;
      const panes = [
        {layerKey: nextLayerKey, cursor: update.date ?? imageRefs[0].cursor},
        {
          layerKey: namesLayer ? nextLayerKey : (compareLayerKey ?? nextLayerKey),
          cursor: update.compareDate ?? (mode === 'COMPARE' ? imageRefs[1]?.cursor : null),
        },
      ]
        .filter((pane): pane is mapUtils.MapImageRefWithCursor => !!pane.cursor)
        .sort((a, b) => a.cursor.localeCompare(b.cursor));

      const nextMode: Mode = panes.length > 1 ? 'COMPARE' : 'VIEW';
      if (nextMode !== mode) {
        mapStateDispatch({type: 'SET_MODE', mode: nextMode});
      }
      mapStateDispatch({
        type: 'SET_IMAGE_REFS',
        imageRefs: (panes.length
          ? panes
          : [{layerKey: nextLayerKey, cursor: null}]) as mapUtils.MapImageRefs,
      });

      const layerSummary = panes.some((pane) => pane.layerKey !== panes[0].layerKey)
        ? panes.map((pane) => pane.layerKey).join(' vs ')
        : nextLayerKey;

      // The reducer snaps cursors to the nearest scene that actually exists, so
      // report the request rather than claiming an exact date.
      return `Asked the map for ${layerSummary}${
        panes.length ? ` at ${panes.map((pane) => pane.cursor).join(' vs ')}` : ''
      }.`;
    },
    [imageRefs, mode, mapStateDispatch]
  );

  const openPane = React.useCallback(
    ({pane}: {pane: 'notes' | 'details' | 'order' | 'analyze'}) => {
      if (pane === 'analyze') {
        openAnalyzeArea();
        return;
      }
      setTabbedSidebarState((prev) => ({
        ...prev,
        currentViewTitle: PANE_TITLES[pane],
        isExpanded: true,
      }));
    },
    [openAnalyzeArea, setTabbedSidebarState]
  );

  return React.useMemo(
    () => ({
      getCurrentView,
      flyTo: mapboxMap ? flyTo : undefined,
      selectProperty,
      setLayerAndDate,
      openPane,
      findProperty,
      describeProperty,
      focusNote,
      focusedNoteId: focusedNoteId === null ? null : String(focusedNoteId),
    }),
    [
      getCurrentView,
      flyTo,
      mapboxMap,
      selectProperty,
      setLayerAndDate,
      openPane,
      findProperty,
      describeProperty,
      focusNote,
      focusedNoteId,
    ]
  );
}
